import React, { useEffect, useState, useCallback } from 'react';
import { 
    Box, 
    Typography, 
    Container, 
    Paper, 
    TextField, 
    Autocomplete, 
    Button, 
    Grid, 
    Rating, 
    Divider, 
    CircularProgress, 
    Chip,
    Link as MuiLink,
    Alert,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { Helmet } from 'react-helmet';
import { useParams, useNavigate } from 'react-router-dom';
import { LocationOn, Search, CalendarMonth, WbSunny, LocalBar } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import TopNav from './TopNav';
import Fonts from './Fonts';
import { themeColors } from '../styles/theme';
import { searchCities, getSunnyPubsByCity } from '../API';
import debounce from 'lodash/debounce';
import AdSense from './AdSense';
import ScrollIndicator from './ScrollIndicator';
import IntensityTimeline from './IntensityTimeline';
import { PubObject } from './FindPubs';
import { SunIcon } from "./SunIcon";

interface City {
    city: string;
    verified_gardens: number;
}

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: '24px',
    marginBottom: '20px',
    borderRadius: '16px',
    background: 'rgba(255, 255, 255, 0.9)',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    border: `1px solid ${themeColors.orange}20`,
}));

const PubCard = styled(Paper)(({ theme }) => ({
    padding: '20px',
    borderRadius: '12px',
    transition: 'transform 0.2s, box-shadow 0.2s',
    cursor: 'pointer',
    background: 'rgba(255, 255, 255, 0.8)',
    position: 'relative',
    overflow: 'hidden',
    '&:hover': {
        transform: 'translateY(-3px)',
        boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
    },
    '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '4px',
        height: '100%',
        background: `linear-gradient(to bottom, ${themeColors.orange}, ${themeColors.mainYellow})`,
    }
}));

const RankBadge = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: '12px',
    right: '12px',
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '1rem',
    fontWeight: 'bold',
    color: '#FFF',
    zIndex: 2,
}));

const SearchButton = styled(Button)(({ theme }) => ({
    background: themeColors.orange,
    color: 'white',
    borderRadius: '28px',
    fontWeight: 600,
    padding: '10px 24px',
    textTransform: 'none',
    fontSize: '1rem',
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
    '&:hover': {
        background: themeColors.mainYellow,
        boxShadow: '0 6px 15px rgba(0,0,0,0.15)',
        transform: 'translateY(-2px)'
    }
}));

const CityLandingPage: React.FC = () => {
    const { encodedCity } = useParams<{ encodedCity: string }>();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    const [cityName, setCityName] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [sunniestPubs, setSunniestPubs] = useState<PubObject[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [citySuggestions, setCitySuggestions] = useState<City[]>([]);
    const [selectedCity, setSelectedCity] = useState<City | null>(null);
    const [resultDate, setResultDate] = useState<string | null>(null);
    const [searchLoading, setSearchLoading] = useState<boolean>(false);
    const [canonicalUrl, setCanonicalUrl] = useState<string>('');
    
    // Decode the city name from the URL
    const getDecodedCityName = (): string => {
        if (!encodedCity) return '';
        try {
            return decodeURIComponent(encodedCity).replace(/<[^>]*>/g, '');
        } catch (e) {
            console.error('Error decoding city name:', e);
            return encodedCity.replace(/%20/g, ' ')
                .replace(/%28/g, '(')
                .replace(/%29/g, ')')
                .replace(/%27/g, "'")
                .replace(/%26/g, '&');
        }
    };
    
    // Fetch city data and top pubs
    useEffect(() => {
        if (!encodedCity) return;
        
        const fetchCityData = async () => {
            setLoading(true);
            setErrorMessage(null);
            
            try {
                const readable = getDecodedCityName();
                setCityName(readable);
                
                const response = await getSunnyPubsByCity(readable, 10);
                
                if (response.data && response.data.pubs && response.data.pubs.length > 0) {
                    setSunniestPubs(response.data.pubs);
                    setResultDate(response.data.date);
                } else {
                    setErrorMessage(`No pubs with garden sun data found in ${readable}.`);
                    setSunniestPubs([]);
                }
            } catch (error) {
                console.error('Error fetching city data:', error);
                setErrorMessage('Unable to load pub data for this city. Please try again later.');
            } finally {
                setLoading(false);
            }
        };
        
        fetchCityData();
    }, [encodedCity]);
    
    // Handle city search
    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(event.target.value);
    };
    
    // Debounced search function
    const debouncedSearch = useCallback(
        debounce((query: string) => {
            if (!query || query.length < 2) {
                setCitySuggestions([]);
                setSearchLoading(false);
                return;
            }
            
            setSearchLoading(true);
            
            searchCities(query)
                .then(response => {
                    if (response.data && response.data.cities) {
                        setCitySuggestions(response.data.cities);
                    } else {
                        setCitySuggestions([]);
                    }
                })
                .catch(err => {
                    console.error('Error searching cities:', err);
                    setCitySuggestions([]);
                })
                .finally(() => {
                    setSearchLoading(false);
                });
        }, 300),
        []
    );
    
    // Trigger search when query changes
    useEffect(() => {
        debouncedSearch(searchQuery);
    }, [searchQuery, debouncedSearch]);
    
    // Handle city selection
    const handleCitySelect = (city: City | null) => {
        if (city) {
            setSelectedCity(city);
            const encodedCityName = encodeURIComponent(city.city);
            navigate(`/sunny-pubs-in/${encodedCityName}`);
        }
    };
    
    // Handle navigation to pub details
    const handleViewPub = (pub: PubObject) => {
        localStorage.setItem('currentViewPubGPId', pub.google_place_id);
        navigate(`/pub/${pub.google_place_id}`, { state: { pubToView: pub } });
    };
    
    // Format date for display
    const formatDate = (dateString: string | null) => {
        if (!dateString) return '';
        
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-GB', { 
                day: 'numeric', 
                month: 'long', 
                year: 'numeric' 
            });
        } catch (e) {
            return dateString;
        }
    };
    
    // Set canonical URL
    useEffect(() => {
        if (cityName) {
            const baseUrl = window.location.origin;
            const path = window.location.pathname;
            setCanonicalUrl(`${baseUrl}${path}`);
        }
    }, [cityName]);
    
    // Generate SEO metadata
    const generateSeoMetadata = () => {
        const formattedDate = formatDate(resultDate);
        const topPubName = sunniestPubs.length > 0 ? sunniestPubs[0].name : '';
        const pubCount = loading ? '' : sunniestPubs.length; // Don't show count while loading
        
        return {
            title: loading 
                ? `Sunniest Pub Gardens in ${cityName}` 
                : `Top ${pubCount} Sunniest Pub Gardens in ${cityName} (${formattedDate})`,
            description: loading
                ? `Discover the sunniest pub gardens in ${cityName}. Our sun tracking technology shows exactly which beer gardens get the most sunshine and the best times to visit.`
                : `RANKED: Discover the ${pubCount} sunniest pub gardens in ${cityName}, including ${topPubName}. Our sun tracking technology shows exactly which beer gardens get the most sunshine and the best times to visit.`,
            pubCount: pubCount,
            dateFormatted: formattedDate,
            topPubName: topPubName,
            keywords: `sunny pubs, ${cityName} pubs, beer gardens, outdoor pubs, sunshine ranking, ${cityName} beer gardens, pub gardens with sun, ${topPubName || 'best pubs'}, ${cityName} sunny spots`
        };
    };
    
    const seoData = generateSeoMetadata();
    
    // Get background color based on rank
    const getRankBgColor = (index: number) => {
        if (index === 0) return '#FFD700'; // Gold
        if (index === 1) return '#C0C0C0'; // Silver
        if (index === 2) return '#CD7F32'; // Bronze
        return themeColors.blue; // Default
    };
    
    return (
        <Box sx={{ width: '100%', minHeight: '100vh' }}>
            <Fonts />
            <Helmet>
                <title>{seoData.title}</title>
                <meta name="description" content={seoData.description} />
                <meta name="keywords" content={seoData.keywords} />
                <link rel="canonical" href={canonicalUrl} />
                
                {/* Add noindex while loading to prevent crawlers from indexing incomplete content */}
                {loading && <meta name="robots" content="noindex" />}
                
                {/* Open Graph tags */}
                <meta property="og:title" content={loading 
                    ? `Sunniest Pubs in ${cityName} | Ranked By Sunshine` 
                    : `Top ${sunniestPubs.length} Sunniest Pubs in ${cityName} | Ranked By Sunshine`} />
                <meta property="og:description" content={seoData.description} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:image" content={`${window.location.origin}/images/sunny-pub-gardens.jpg`} />
                <meta property="og:site_name" content="Suntap" />
                <meta property="og:locale" content="en_GB" />
                
                {/* Twitter Card tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={loading 
                    ? `Sunniest Pubs in ${cityName} | Ranked By Sunshine` 
                    : `Top ${sunniestPubs.length} Sunniest Pubs in ${cityName} | Ranked By Sunshine`} />
                <meta name="twitter:description" content={seoData.description} />
                <meta name="twitter:image" content={`${window.location.origin}/images/sunny-pub-gardens.jpg`} />
                
                {/* JSON-LD structured data for search engines - only when data is loaded */}
                {!loading && sunniestPubs.length > 0 && (
                    <script type="application/ld+json">
                        {JSON.stringify({
                            "@context": "https://schema.org",
                            "@type": "CollectionPage",
                            "headline": `Top ${sunniestPubs.length} Sunniest Pub Gardens in ${cityName}`,
                            "description": seoData.description,
                            "url": canonicalUrl,
                            "datePublished": seoData.dateFormatted,
                            "mainEntity": {
                                "@type": "ItemList",
                                "numberOfItems": sunniestPubs.length,
                                "itemListElement": sunniestPubs.map((pub, index) => ({
                                    "@type": "ListItem",
                                    "position": index + 1,
                                    "item": {
                                        "@type": "LocalBusiness",
                                        "name": pub.name,
                                        "address": pub.address,
                                        "description": `#${index + 1} sunniest pub garden in ${cityName} with excellent sun exposure throughout the day`,
                                        "geo": {
                                            "@type": "GeoCoordinates",
                                            "latitude": pub.latitude,
                                            "longitude": pub.longitude
                                        },
                                        "review": {
                                            "@type": "Review",
                                            "reviewRating": {
                                                "@type": "Rating",
                                                "ratingValue": pub.rating || 4,
                                                "bestRating": 5
                                            },
                                            "name": `Sun rating for ${pub.name}`,
                                            "author": {
                                                "@type": "Organization",
                                                "name": "Suntap"
                                            }
                                        }
                                    }
                                }))
                            }
                        })}
                    </script>
                )}
            </Helmet>
            
            <TopNav title={`Sunny Pubs in ${cityName}`} />
            
            <Container 
                maxWidth="lg" 
                sx={{ 
                    pt: '16px',
                    pb: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                {/* Main Heading with Date Badge */}
                <Box sx={{ 
                    width: '100%',
                    maxWidth: 800,
                    mx: 'auto',
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    position: 'relative',
                    mb: 3
                }}>
                    <h1 style={{ 
                        fontFamily: "'DM Sans', sans-serif",
                        color: '#222',
                        fontWeight: 700,
                        marginBottom: '8px',
                        fontSize: isMobile ? '1.8rem' : '2.5rem',
                        textShadow: '1px 1px 2px rgba(0,0,0,0.08)',
                        background: `linear-gradient(45deg, ${themeColors.orange}, #222)`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        padding: '0 16px'
                    }}>
                        {loading ? `Sunniest Pubs in ${cityName}` : `Top ${sunniestPubs.length} Sunniest Pubs in ${cityName}`}
                    </h1>
                    
                    {resultDate && (
                        <Chip
                            icon={<CalendarMonth sx={{ fontSize: 16 }} />}
                            label={`Updated ${formatDate(resultDate)}`}
                            variant="outlined"
                            size="small"
                            sx={{ 
                                bgcolor: 'rgba(255, 180, 0, 0.1)', 
                                borderColor: 'rgba(255, 180, 0, 0.3)',
                                color: themeColors.orange,
                                fontWeight: 500,
                                fontSize: '0.75rem',
                                mb: 3
                            }}
                        />
                    )}
                </Box>

                {/* Loading State */}
                {loading && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
                        <CircularProgress sx={{ color: themeColors.orange, mb: 2 }} />
                        <Typography variant="body1" sx={{ color: '#666' }}>
                            Finding the sunniest spots in {cityName}...
                        </Typography>
                    </Box>
                )}
                
                {/* Error Message */}
                {errorMessage && !loading && (
                    <StyledPaper 
                        elevation={1}
                        sx={{
                            bgcolor: 'rgba(255, 235, 235, 0.5)',
                            border: '1px solid rgba(255, 0, 0, 0.1)',
                            textAlign: 'center',
                            py: 4,
                            width: '100%',
                            maxWidth: 800
                        }}
                    >
                        <Typography color="error" variant="h6" sx={{ mb: 2 }}>
                            {errorMessage}
                        </Typography>
                        <Typography variant="body1">
                            Try searching for another city or visit our <MuiLink href="/" underline="hover">home page</MuiLink> to explore other options.
                        </Typography>
                    </StyledPaper>
                )}
                
                {/* Sunny Pubs List - THE MAIN CONTENT */}
                {!loading && !errorMessage && sunniestPubs.length > 0 && (
                    <Box sx={{ width: '100%', maxWidth: 800 }}>
                        {/* Brief intro above the list */}
                        <Typography 
                            variant="body1" 
                            sx={{ 
                                color: '#444',
                                mb: 2,
                                textAlign: 'center',
                                fontSize: '1.1rem',
                                fontWeight: 500
                            }}
                        >
                            Discover sunshine hotspots in {cityName} - ranked using our sun tracking technology.
                            <br />
                            Click any pub to see detailed sun predictions and sunny hours.
                        </Typography>
                        
                        {/* Pubs List */}
                        <Grid container spacing={2.5}>
                            {sunniestPubs.map((pub, index) => (
                                <Grid item xs={12} key={pub.google_place_id}>
                                    <PubCard elevation={2} onClick={() => handleViewPub(pub)}>
                                        {/* Rank Badge */}
                                        <RankBadge sx={{ 
                                            bgcolor: getRankBgColor(index),
                                            boxShadow: index < 3 ? '0 2px 8px rgba(0,0,0,0.2)' : 'none'
                                        }}>
                                            {index + 1}
                                        </RankBadge>
                                        
                                        <Box sx={{ 
                                            display: 'flex', 
                                            flexDirection: { xs: 'column', sm: 'row' },
                                            alignItems: { xs: 'flex-start', sm: 'center' },
                                            justifyContent: 'space-between',
                                            mb: 0.5,
                                            pl: 1
                                        }}>
                                            <Typography 
                                                variant="h6" 
                                                component="h2"
                                                sx={{ 
                                                    fontWeight: 600,
                                                    color: index < 3 ? themeColors.orange : themeColors.darkBackground,
                                                    fontFamily: "'DM Sans', sans-serif",
                                                    fontSize: { xs: '1.1rem', sm: '1.2rem' },
                                                    mr: 6, // Space for the rank badge
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    '& svg': { mr: 1, color: index < 3 ? '#FFD700' : themeColors.blue }
                                                }}
                                            >
                                                {index < 3 && <WbSunny fontSize="small" />} 
                                                {pub.name}
                                            </Typography>
                                            
                                            <Box sx={{ 
                                                display: 'flex', 
                                                alignItems: 'center',
                                                mt: { xs: 1, sm: 0 }
                                            }}>
                                                <Rating
                                                    name={`rating-${pub.google_place_id}`}
                                                    value={pub?.rating ?? 0}
                                                    readOnly
                                                    icon={<SunIcon fontSize="inherit" sx={{ color: '#FFD700' }} />}
                                                    emptyIcon={null}
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'row',
                                                        '& .MuiRating-icon': {
                                                            marginRight: '2px'
                                                        }
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                        
                                        <Typography 
                                            variant="body2" 
                                            sx={{ 
                                                color: '#666', 
                                                mb: 1.5,
                                                display: 'flex',
                                                alignItems: 'center',
                                                pl: 1
                                            }}
                                        >
                                            <LocationOn sx={{ color: '#999', fontSize: 16, mr: 0.5 }} />
                                            {pub.address}
                                        </Typography>
                                        
                                        {/* Display sun timeline */}
                                        {pub.heatmap && (
                                            <Box sx={{ my: 1.5, height: '70px' }}>
                                                <Typography 
                                                    variant="caption" 
                                                    sx={{ 
                                                        color: themeColors.blue,
                                                        mb: 0.5,
                                                        display: 'block',
                                                        pl: 1
                                                    }}
                                                >
                                                    Today's Sunshine Forecast:
                                                </Typography>
                                                <IntensityTimeline 
                                                    data={{
                                                        timestamps: pub.heatmap.timestamps,
                                                        values: { [pub.google_place_id]: pub.heatmap.values }
                                                    }} 
                                                    googlePlaceId={pub.google_place_id}
                                                    mode={isMobile ? 'compact' : 'full'}
                                                />
                                            </Box>
                                        )}
                                        
                                        {/* Increased margin-top (mt) from 2 to 3.5 to add more space */}
                                        <Box sx={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between', 
                                            alignItems: 'center', 
                                            mt: 3.5,
                                            pl: 1
                                        }}>
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    color: themeColors.orange,
                                                    fontWeight: 600,
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                }}
                                            >
                                                <LocalBar sx={{ fontSize: 16, mr: 0.5 }} />
                                                View detailed sun forecast
                                            </Typography>
                                            
                                            {pub.gardens && pub.gardens.length > 0 && (
                                                <Chip 
                                                    size="small" 
                                                    label={`${pub.gardens.length} ${pub.gardens.length === 1 ? 'garden' : 'gardens'}`}
                                                    sx={{ 
                                                        bgcolor: 'rgba(76, 175, 80, 0.1)',
                                                        color: '#4caf50',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 500
                                                    }} 
                                                />
                                            )}
                                        </Box>
                                    </PubCard>
                                    
                                    {/* Ad after the 3rd item */}
                                    {index === 2 && (
                                        <Box sx={{ my: 3, width: '100%' }}>
                                            <AdSense 
                                                client="ca-pub-9983613093656329"
                                                slot="2973327869"
                                                format="auto"
                                                responsive="true"
                                                width="100%"
                                                height="250px"
                                            />
                                        </Box>
                                    )}
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}
                
                {/* Search Box - moved below pub list */}
                {!loading && (
                    <StyledPaper elevation={2} sx={{ width: '100%', maxWidth: 800, mt: 4 }}>
                        <Typography 
                            variant="h6" 
                            component="h2"
                            sx={{ 
                                mb: 2,
                                fontFamily: "'DM Sans', sans-serif",
                                color: themeColors.darkBackground,
                                fontWeight: 600
                            }}
                        >
                            Explore Other Cities' Sunniest Spots
                        </Typography>
                        
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: 'center',
                            gap: 1.5
                        }}>
                            <Autocomplete
                                fullWidth
                                freeSolo
                                loading={searchLoading}
                                options={citySuggestions}
                                getOptionLabel={(option) => typeof option === 'string' ? option : option.city}
                                renderOption={(props, option) => (
                                    <Box component="li" {...props} key={option.city} sx={{ py: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <LocationOn sx={{ color: themeColors.blue, mr: 1 }} />
                                                <Typography variant="body1">{option.city}</Typography>
                                            </Box>
                                            <Chip 
                                                size="small" 
                                                label={`${option.verified_gardens} gardens`}
                                                sx={{ 
                                                    bgcolor: 'rgba(60, 104, 169, 0.1)',
                                                    color: themeColors.blue,
                                                    fontSize: '0.7rem',
                                                    fontWeight: 500
                                                }} 
                                            />
                                        </Box>
                                    </Box>
                                )}
                                onChange={(_, value) => {
                                    if (value && typeof value !== 'string') {
                                        handleCitySelect(value);
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Search cities"
                                        variant="outlined"
                                        onChange={handleSearchChange}
                                        placeholder="Type a city name..."
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <Search color="action" sx={{ ml: 1, mr: 0.5 }} />
                                            ),
                                            endAdornment: (
                                                <>
                                                    {searchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                            sx: {
                                                borderRadius: '12px',
                                                '&.Mui-focused': {
                                                    borderColor: themeColors.orange
                                                }
                                            }
                                        }}
                                    />
                                )}
                            />
                            
                            <SearchButton
                                variant="contained"
                                disabled={!selectedCity}
                                onClick={() => handleCitySelect(selectedCity)}
                                sx={{ 
                                    minWidth: { xs: '100%', sm: '140px' },
                                    height: { xs: 'auto', sm: '56px' }
                                }}
                            >
                                Search City
                            </SearchButton>
                        </Box>
                    </StyledPaper>
                )}
                
                {/* Brief About Section with Call to Action */}
                <StyledPaper elevation={2} sx={{ width: '100%', maxWidth: 800, mt: 3 }}>
                    <Typography 
                        variant="h6" 
                        component="h2"
                        sx={{ 
                            mb: 2,
                            fontFamily: "'DM Sans', sans-serif",
                            color: themeColors.darkBackground,
                            fontWeight: 600
                        }}
                    >
                        About Our Sunshine Rankings
                    </Typography>
                    
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Our unique technology calculates precisely which parts of pub gardens receive sunshine throughout the day.
                        We use advanced 3D modeling and solar position algorithms to predict sun and shade patterns with incredible accuracy.
                    </Typography>
                    
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Each venue is ranked based on its total sunshine hours. The beer mug ratings (☀️) show sunshine intensity,
                        while our detailed timelines reveal exactly when to visit for maximum sun exposure.
                    </Typography>
                    
                    <Box sx={{ 
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 2,
                        mt: 3
                    }}>
                        <Button
                            variant="outlined"
                            fullWidth
                            href="/submit"
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                borderRadius: '12px',
                                padding: '12px 20px',
                                borderColor: themeColors.orange,
                                color: themeColors.orange,
                                '&:hover': {
                                    borderColor: themeColors.orange,
                                    backgroundColor: 'rgba(252, 82, 8, 0.04)',
                                }
                            }}
                        >
                            Submit a Pub Garden in {cityName}
                        </Button>

                        <Button
                            variant="outlined"
                            fullWidth
                            href="/pubs"
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                borderRadius: '12px',
                                padding: '12px 20px',
                                borderColor: themeColors.blue,
                                color: themeColors.blue,
                                '&:hover': {
                                    borderColor: themeColors.blue,
                                    backgroundColor: 'rgba(60, 104, 169, 0.04)',
                                }
                            }}
                        >
                            View All Sunny Pubs Near Me
                        </Button>
                    </Box>
                </StyledPaper>
            </Container>
            
            <ScrollIndicator 
                forceVisible={true}
                iconColor={themeColors.orange}
                backgroundColor="rgba(255, 255, 255, 0.8)"
            />
        </Box>
    );
};

export default CityLandingPage;